import { Component, OnInit, inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GolfTechType } from 'src/app/shared/models/golftech-type.enum';
import { AuthService } from '@auth0/auth0-angular';
import { Auth0AuthenticationService } from 'src/app/core/auth0-authentication.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarNotificationComponent } from 'src/app/shared/ui/snackbar-notification/snackbar-notification/snackbar-notification.component';
import { SimulatorBookingModule } from 'src/app/simulator-booking/simulator-booking.module';
import { SharedModule } from 'src/app/shared/shared.module';
import { GtStripeService } from 'src/app/shared/services/gt-stripe.service';
import { injectStripe, StripeCardComponent } from 'ngx-stripe';
import { StripeCardElementOptions, StripeElementsOptions } from '@stripe/stripe-js';
import { IdentityUserService } from 'src/app/shared/services/identity/identity-user.service';
import { BaseSignUpComponent } from '../base-signup-component';

@Component({
    selector: 'sign-up-simulator',
    templateUrl: './sign-up-simulator.component.html',
    styleUrls: ['./sign-up-simulator.component.scss'],
    standalone: true,
    imports: [
        SharedModule,
        SimulatorBookingModule
    ]
})
export class SignUpSimulatorComponent extends BaseSignUpComponent implements OnInit {
    constructor(fb: FormBuilder,
        private auth: AuthService,
        private authService: Auth0AuthenticationService,
        private snackBar: MatSnackBar,
    ) { 
        super(fb);
    }

    ngOnInit(): void {
        this.billingForm = this.fb.group({
            address: ['', Validators.required],
            city: ['', Validators.required],
            state: ['', Validators.required],
            zip: ['', [Validators.required, Validators.min(10000), Validators.max(99999)]],
            plan: ['', Validators.required],
            simulatorCount: ['', [Validators.required, Validators.min(1)]]
        });
        // Fetch plans for simulators
        this.stripeService.getProducts({platform: "simbook"}).subscribe({
            next: (plans: any) => {
                this.plans = plans.data.filter((plan: any) => plan.metadata.custom === "false");
                this.filteredPlans = this.plans;
            },
            error: (err: any) => {
                console.log(err);
            }
        });
    }



    getMaxSimulatorCount(): number {
        if(this.plans.length === 0 || !this.billingForm.get('plan')?.value) return 1;
        const selectedPlan = this.plans.find(plan => plan.id === this.billingForm.get('plan')?.value);
        const permissions = JSON.parse(selectedPlan?.metadata?.permissions);
        if (permissions?.simulatorCount) {
            return permissions?.simulatorCount;
        }
        return 1;
    }

    onPlanChange(): void {
        // Reset simulator count when plan changes
        this.billingForm.patchValue({ simulatorCount: '' });
        
        // Update simulator count validation based on selected plan
        const maxCount = this.getMaxSimulatorCount();
        const simulatorCountControl = this.billingForm.get('simulatorCount');
        if (simulatorCountControl) {
            simulatorCountControl.setValidators([
                Validators.required, 
                Validators.min(1), 
                Validators.max(maxCount)
            ]);
            simulatorCountControl.updateValueAndValidity();
        }
    }

    submit(): void {
        if (this.accountForm.invalid || this.billingForm.invalid) return;
        this.isLoading = true;
        const userData = this.accountForm.value;
        const billingData = this.billingForm.value;

        userData.metadata = {
            firstName: userData.given_name,
            lastName: userData.family_name,
            golfTechType: GolfTechType.Simulator,
        }

        this.authService.signup(userData).subscribe({
            next: (auth0Response: any) => {
                this.onSignUpSuccessCreatePaymentMethod(auth0Response);
            },
            error: (err: any) => {
                console.log(err);
                this.isLoading = false;
                let errorMessage;
                if (err.error.code === "invalid_password") {
                    errorMessage = err.error.policy;
                }

                if (err.error.code === "invalid_signup") {
                    errorMessage = "The user already exists.";
                }

                errorMessage = errorMessage.replaceAll("\n", "<br />");

                let data = {
                    text:  errorMessage, 
                    useInnerHtml: true
                }

                this.snackBar.openFromComponent(SnackbarNotificationComponent, {
                    duration: undefined,
                    horizontalPosition: 'center',
                    verticalPosition: 'top',
                    data: data
                });
            }
        });
    }

    onSignUpSuccessCreatePaymentMethod(auth0Response: any) {
        this.stripe.createPaymentMethod({
            type: 'card',
            card: this.cardElement.element,
            billing_details: {
                name: `${this.accountForm.value.given_name} ${this.accountForm.value.family_name}`,
                email: this.accountForm.value.email,
                address: {
                    line1: this.billingForm.value.address,
                    city: this.billingForm.value.city,
                    state: this.billingForm.value.state,
                    postal_code: this.billingForm.value.zip,
                    country: 'US'
                }
            }
        }).subscribe({
            next: (res: any) => {
                this.onCreatePaymentMethodSuccess(auth0Response, res);
            },
            error: (err: any) => {
                console.log(err);
                this.isLoading = false;
            }
        });
    }

    onCreatePaymentMethodSuccess(auth0Response: any, res: any) {
        this.identityUserService.createIdentityUser({
            auth0Id: `auth0|${auth0Response._id}`,
            firstName: this.accountForm.value.given_name,
            lastName: this.accountForm.value.family_name,
            golfTechType: GolfTechType.Simulator.toString(),
            email: this.accountForm.value.email,
            stripeProductId: this.billingForm.value.plan,
            paymentMethodId: res.paymentMethod.id,
            simulatorCount: this.billingForm.value.simulatorCount
        }).subscribe({
            next: (res: any) => {
                console.log(res);
                this.isLoading = false;
                this.snackBar.openFromComponent(SnackbarNotificationComponent, {
                    duration: 10000,
                    horizontalPosition: 'center',
                    verticalPosition: 'top',
                    data: { text: "User created successfully, you will now be redirected to the login page in 10 seconds" }
                });
                setTimeout(() => {
                    this.auth.loginWithRedirect();
                }, 10000);
            },
            error: (err: any) => {
                console.log(err);
                this.isLoading = false;
                this.snackBar.openFromComponent(SnackbarNotificationComponent, {
                    duration: 10000,
                    horizontalPosition: 'center',
                    verticalPosition: 'top',
                    data: { text: "There was an error creating the user, please try again or contact support." }
                });
            }
        });
    }
}  